/**
 * Campo do código de 6 dígitos da redefinição de senha — spec 06.
 *
 * Seis caixas em vez de um input só. O motivo é prático: a pessoa está lendo o
 * código do celular e digitando no computador, e caixas separadas mostram
 * quantos dígitos faltam sem ela precisar contar.
 *
 * O que fica embaixo do capô e importa:
 * - colar o código inteiro preenche as seis caixas de uma vez (é o gesto mais
 *   comum: copiar do e-mail);
 * - apagar numa caixa vazia volta para a anterior;
 * - só dígito entra, e o campo é `inputMode="numeric"` para o teclado do
 *   tablet abrir no numérico.
 */
import { useEffect, useId, useRef } from 'react'
import type { ClipboardEvent, KeyboardEvent } from 'react'

const DIGITOS = 6

export function CampoCodigo({
  valor,
  aoMudar,
  erro,
  aoCompletar,
}: {
  valor: string
  aoMudar: (codigo: string) => void
  erro?: string
  /** Chamado quando as seis caixas ficam preenchidas. */
  aoCompletar?: (codigo: string) => void
}) {
  const id = useId()
  const idErro = `${id}-erro`
  const caixas = useRef<Array<HTMLInputElement | null>>([])

  const digitos = valor.padEnd(DIGITOS, ' ').slice(0, DIGITOS).split('')

  useEffect(() => {
    if (valor.length === DIGITOS) aoCompletar?.(valor)
    // aoCompletar fora das dependências de propósito: incluí-lo faria o efeito
    // disparar de novo a cada render do pai que redefine a função.
  }, [valor])

  function escrever(posicao: number, texto: string) {
    const digito = texto.replace(/\D/g, '').slice(-1)
    if (digito === '') return

    const proximo = digitos.map((d, i) => (i === posicao ? digito : d.trim())).join('')
    aoMudar(proximo.slice(0, DIGITOS))

    caixas.current[posicao + 1]?.focus()
  }

  function tecla(posicao: number, evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key === 'Backspace') {
      evento.preventDefault()

      const atual = digitos[posicao]?.trim() ?? ''
      if (atual !== '') {
        aoMudar(digitos.map((d, i) => (i === posicao ? '' : d.trim())).join(''))
        return
      }
      // Caixa vazia: volta e apaga a anterior, que é o que a pessoa espera.
      aoMudar(digitos.map((d, i) => (i === posicao - 1 ? '' : d.trim())).join(''))
      caixas.current[posicao - 1]?.focus()
      return
    }

    if (evento.key === 'ArrowLeft') caixas.current[posicao - 1]?.focus()
    if (evento.key === 'ArrowRight') caixas.current[posicao + 1]?.focus()
  }

  function colar(evento: ClipboardEvent<HTMLInputElement>) {
    evento.preventDefault()

    const colado = evento.clipboardData.getData('text').replace(/\D/g, '').slice(0, DIGITOS)
    if (colado === '') return

    aoMudar(colado)
    caixas.current[Math.min(colado.length, DIGITOS - 1)]?.focus()
  }

  return (
    <div>
      <label htmlFor={`${id}-0`} className="mb-2 block text-[13px] font-semibold text-texto">
        Código do e-mail
      </label>

      <div className="flex gap-2" role="group" aria-describedby={erro ? idErro : undefined}>
        {digitos.map((digito, posicao) => (
          <input
            key={posicao}
            ref={(elemento) => {
              caixas.current[posicao] = elemento
            }}
            id={`${id}-${posicao}`}
            value={digito.trim()}
            onChange={(e) => escrever(posicao, e.target.value)}
            onKeyDown={(e) => tecla(posicao, e)}
            onPaste={colar}
            onFocus={(e) => e.target.select()}
            inputMode="numeric"
            autoComplete={posicao === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            aria-label={`Dígito ${posicao + 1} de ${DIGITOS}`}
            aria-invalid={erro ? true : undefined}
            className={`h-[54px] w-full rounded-auth border text-center font-titulo text-[22px] font-bold tabular text-titulo ${
              erro ? 'border-erro' : 'border-borda'
            }`}
          />
        ))}
      </div>

      {erro && (
        <p id={idErro} role="alert" className="mt-[6px] text-[12.5px] font-medium text-erro">
          {erro}
        </p>
      )}
    </div>
  )
}
